/**
 * DataListItem — аналог antd List.Item.
 *
 * Підтримує:
 *  - children — основний вміст
 *  - actions  — масив дій (кнопок), відображається знизу (vertical) або справа (horizontal)
 *  - extra    — додатковий вміст (справа або знизу залежно від itemLayout)
 *  - Усі стандартні HTML атрибути елемента списку (onClick, style, className, тощо)
 */

import React, { type CSSProperties, type ReactNode } from 'react'
import { useDataListContext } from './DataListContext'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DataListItemProps
    extends Omit<React.HTMLAttributes<HTMLLIElement>, 'prefix'> {
    /** Масив дій (кнопок) для елемента */
    actions?: ReactNode[]
    /** Додатковий вміст (наприклад, прев'ю зображення) */
    extra?: ReactNode
    className?: string
    style?: CSSProperties
    children?: ReactNode
}

// ─── Sub-component: Meta ──────────────────────────────────────────────────────

export interface DataListItemMetaProps {
    /** Аватар / іконка */
    avatar?: ReactNode
    /** Заголовок */
    title?: ReactNode
    /** Опис */
    description?: ReactNode
    className?: string
    style?: CSSProperties
}

const DataListItemMeta: React.FC<DataListItemMetaProps> = ({
    avatar,
    title,
    description,
    className = '',
    style,
}) => {
    const rootClass = ['data-list-item__meta', className].filter(Boolean).join(' ')

    return (
        <div className={rootClass} style={style}>
            {avatar && <div className="data-list-item__meta-avatar">{avatar}</div>}
            <div className="data-list-item__meta-content">
                {title && <h4 className="data-list-item__meta-title">{title}</h4>}
                {description && (
                    <div className="data-list-item__meta-description">{description}</div>
                )}
            </div>
        </div>
    )
}

// ─── Component ────────────────────────────────────────────────────────────────

type DataListItemComponent = React.FC<DataListItemProps> & {
    Meta: typeof DataListItemMeta
}

const DataListItemInner: React.FC<DataListItemProps> = ({
    actions,
    extra,
    className = '',
    style,
    children,
    ...rest
}) => {
    const { components } = useDataListContext()

    // Коріневий елемент: components.item з контексту або стандартний <li>
    const RootElement = (components?.item ?? 'li') as React.ElementType

    const rootClass = ['data-list-item', className].filter(Boolean).join(' ')

    return (
        <RootElement className={rootClass} style={style} {...rest}>
            {/* Основний вміст + extra */}
            <div className="data-list-item__main">
                <div className="data-list-item__content">{children}</div>
                {extra && <div className="data-list-item__extra">{extra}</div>}
            </div>

            {/* Дії */}
            {actions && actions.length > 0 && (
                <ul className="data-list-item__actions">
                    {actions.map((action, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={i} className="data-list-item__action">
                            {action}
                            {i < actions.length - 1 && (
                                <em className="data-list-item__action-split" />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </RootElement>
    )
}

export const DataListItem = DataListItemInner as DataListItemComponent
DataListItem.Meta = DataListItemMeta
